import { useState, useMemo, useEffect } from 'react';
import { ActivityIndicator } from "react-native-paper";
import { View, TextInput, Pressable } from 'react-native';
import { debounce } from 'lodash';
import THEME from '../../config/theme';
import { Feather, Octicons } from '@expo/vector-icons';
import CategoryList from '../CategoryList';
import { FooterText, StandardText, SubTitle } from '../../config/theme/globalStyles';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { useWebSearch } from '../../queries/useWebSearch';

const SearchBar = ({ allowedCardKeys, productCatalogOrigin, user }) => {
  const [searchText, setSearchText] = useState('');
  const [submittedTerm, setSubmittedTerm] = useState('');

  const { customColor, customFont } = useCustomTheme();

  const textColor = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const titleColor = customColor?.text?.app || THEME.COLORS.TITLE_MAIN;
  const accent = customColor?.primary || THEME.COLORS.PRIMARY_800;
  const fontTitle = customFont?.fontFamilyTitle || THEME.FONTFAMILY.BOLD;
  const fontText = customFont?.fontFamilyText || THEME.FONTFAMILY.REGULAR;
  const fontSubtitle = customFont?.fontFamilySubtitle || THEME.FONTFAMILY.MEDIUM;

  const debouncedSubmit = useMemo(
    () => debounce((value) => setSubmittedTerm(value.trim()), 400),
    []
  );

  useEffect(() => () => debouncedSubmit.cancel(), [debouncedSubmit]);

  const { cardsResults, videosResults, loading, searchPerformed } = useWebSearch(
    submittedTerm,
    { allowedCardKeys, user }
  );

  const handleChangeText = (text) => {
    setSearchText(text);
    const trimmed = text.trim();
    if (trimmed.length < 3) {
      debouncedSubmit.cancel();
      setSubmittedTerm('');
      return;
    }
    debouncedSubmit(text);
  };

  const handleClearSearch = () => {
    debouncedSubmit.cancel();
    setSearchText('');
    setSubmittedTerm('');
  };

  const handleSubmit = (text) => {
    debouncedSubmit.cancel();
    const trimmed = (text ?? searchText).trim();
    setSubmittedTerm(trimmed.length >= 3 ? trimmed : '');
  };

  const totalResults = cardsResults.length + videosResults.length;

  return (
    <>
      <View style={{ paddingHorizontal: '1.5rem' }}>
        <SubTitle
          textAlign="left"
          color={titleColor}
          margin="0rem 0rem 1rem 0rem"
          style={{ fontFamily: fontTitle }}
        >
          Buscar
        </SubTitle>
        {productCatalogOrigin?.title && (
          <FooterText
            padding="0rem 0rem 0.75rem"
            color={`${textColor}CC`}
            style={{ fontFamily: fontText }}
            textAlign="left"
          >
            {`Buscando em: ${productCatalogOrigin.title}`}
          </FooterText>
        )}

        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            height: 48,
            borderWidth: 1.5,
            borderColor: `${textColor}33`,
            borderRadius: 14,
            paddingHorizontal: 14,
            backgroundColor: `${textColor}0D`,
          }}
        >
          <Feather name="search" size={18} color={textColor} />
          <TextInput
            placeholder="Busque por módulo, conteúdo..."
            placeholderTextColor={`${textColor}80`}
            value={searchText}
            onChangeText={handleChangeText}
            returnKeyType="search"
            onSubmitEditing={(event) => handleSubmit(event?.nativeEvent?.text)}
            style={{
              flex: 1,
              height: '100%',
              color: textColor,
              backgroundColor: 'transparent',
              borderWidth: 0,
              outline: 'none',
              fontSize: 15,
              fontFamily: fontSubtitle,
            }}
          />
          {loading ? (
            <ActivityIndicator color={accent} size={16} />
          ) : searchText !== '' ? (
            <Pressable onPress={handleClearSearch} hitSlop={8}>
              <Octicons name="x-circle-fill" size={16} color={`${textColor}99`} />
            </Pressable>
          ) : null}
        </View>

        {!searchPerformed && searchText.trim().length === 0 && (
          <FooterText
            padding="0.75rem 0rem"
            color={`${textColor}CC`}
            style={{ fontFamily: fontText }}
            textAlign="left"
          >
            Para encontrar o conteúdo que você deseja, digite o termo na barra de pesquisa acima.
          </FooterText>
        )}
        {!searchPerformed && searchText.trim().length > 0 && searchText.trim().length < 3 && (
          <FooterText
            padding="0.75rem 0rem"
            color={`${textColor}CC`}
            style={{ fontFamily: fontText }}
            textAlign="left"
          >
            Digite pelo menos 3 caracteres para buscar.
          </FooterText>
        )}

        {searchPerformed && !loading && (
          <FooterText
            padding="0.75rem 0rem 0rem"
            color={`${textColor}99`}
            style={{ fontFamily: fontText }}
            textAlign="left"
          >
            {totalResults > 0
              ? `${totalResults} resultado${totalResults > 1 ? 's' : ''} para "${submittedTerm}"`
              : ''}
          </FooterText>
        )}
      </View>

      {searchPerformed && !loading && (
        <>
          {cardsResults.length > 0 && (
            <>
              <View style={{ paddingHorizontal: '1.5rem', marginTop: '1rem' }}>
                <StandardText
                  fontSize={customFont?.fontMD || THEME.FONTSIZE.MEDIUM}
                  style={{ fontFamily: fontTitle }}
                  color={titleColor}
                  textAlign="left"
                  numberOfLines={1}
                >
                  {`Módulos encontrados (${cardsResults.length})`}
                </StandardText>
              </View>
              <View style={{ paddingHorizontal: '0.5rem' }}>
                <CategoryList
                  category={{ cards: cardsResults }}
                  user={user}
                  productCatalogOrigin={productCatalogOrigin}
                />
              </View>
            </>
          )}

          {videosResults.length > 0 && (
            <>
              <View style={{ paddingHorizontal: '1.5rem', marginTop: '1rem' }}>
                <StandardText
                  fontSize={customFont?.fontMD || THEME.FONTSIZE.MEDIUM}
                  style={{ fontFamily: fontTitle }}
                  color={titleColor}
                  textAlign="left"
                  numberOfLines={1}
                >
                  {`Conteúdos encontrados (${videosResults.length})`}
                </StandardText>
              </View>
              <View style={{ paddingHorizontal: '0.5rem' }}>
                <CategoryList
                  category={{ cards: videosResults }}
                  user={user}
                  productCatalogOrigin={productCatalogOrigin}
                />
              </View>
            </>
          )}

          {totalResults === 0 && (
            <View style={{ paddingHorizontal: '1.5rem', marginTop: '1.5rem', alignItems: 'center' }}>
              <Feather name="search" size={28} color={`${textColor}66`} />
              <FooterText
                padding="0.75rem 0rem 0rem"
                color={`${textColor}CC`}
                textAlign="center"
                style={{ fontFamily: fontText }}
              >
                Nenhum resultado encontrado. Tente buscar por outro termo.
              </FooterText>
            </View>
          )}
        </>
      )}
    </>
  );
};

export default SearchBar;
